<template>
  <div class="page-container">
    <div class="list-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <PageHeader :config="config" @module-click="handleModuleClick" />
      <Breadcrumb :items="breadcrumbItems" />

      <SearchBanner
        search-object="talent"
        :title-main1="headerConfig.titleMain1"
        :title-main2="headerConfig.titleMain2"
        :subtitle="headerConfig.titleSub"
        :placeholder="headerConfig.searchPlaceholder"
        :button-text="headerConfig.searchButton"
        :keyword.sync="searchKeyword"
        :background-image="bannerBg"
        @search="handleSearch"
      />

      <div class="filter-panel content-wrap">
        <div class="field-bar">
          <img class="field-ico" :src="iconField" alt="" @error="onImgError" />
          <span class="field-label">{{ config.fieldFilter?.label }}</span>
          <div class="field-chips">
            <button
              v-for="tab in fieldTabs"
              :key="tab.value === '' ? 'all' : tab.value"
              type="button"
              class="field-chip"
              :class="{ active: activeField === tab.value }"
              @click="handleFieldChange(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="list-wrap content-wrap">
        <div v-if="loading" class="section-empty">{{ paginationConfig.loading }}</div>
        <div v-else-if="list.length" class="card-grid">
          <TechTalentCard
            v-for="item in list"
            :key="item.id"
            :item="item"
            @click="goDetail"
          />
        </div>
        <div v-else class="section-empty">{{ emptyText }}</div>

        <div v-if="totalCount > 0" class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">
              {{ paginationConfig.prevPage }}
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="page-number"
              :class="{ active: currentPage === page }"
              @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">
              {{ paginationConfig.nextPage }}
            </button>
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { getTechTalentNewConfig } from '@/config/techTalentNewConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import TechTalentCard from '@/components/talent/TechTalentCard.vue'
import techTalentApi from '@/api/techTalent'
import supplyDemandApi from '@/api/supplyDemand'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import { CATEGORY_ALL_LABEL } from '@/utils/filterTabs'

export default {
  name: 'TechTalentNew',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    TechTalentCard
  },
  data() {
    return {
      config: {},
      searchKeyword: '',
      breadcrumbItems: [],
      fieldTabs: [],
      activeField: '',
      areaDict: [],
      list: [],
      loading: false,
      currentPage: 1,
      pageSize: 12,
      totalCount: 0
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    emptyText() {
      return getText('common.empty')
    },
    headerConfig() {
      return this.config.header || {}
    },
    paginationConfig() {
      return this.config.pagination || {}
    },
    bannerBg() {
      return getSafeImage(this.headerConfig.bannerBg)
    },
    iconField() {
      return getSafeImage(this.config.icons?.field)
    },
    totalPages() {
      const pages = Math.ceil(this.totalCount / this.pageSize)
      return pages > 0 ? pages : 1
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (total <= max) return Array.from({ length: total }, (_, i) => i + 1)
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },
    pageInfoText() {
      const tpl = this.paginationConfig.pageInfo || '共{total}条，每页显示{pageSize}条'
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    }
  },
  created() {
    this.loadConfig()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },
    getPageScaleEl() {
      return this.$el.querySelector('.list-page')
    },
    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    onImgError(e) {
      handleImgError(e)
    },
    async loadConfig() {
      this.config = getTechTalentNewConfig(getCurrentLocale())
      const bc = this.config.breadcrumb || {}
      this.breadcrumbItems = [
        { label: bc.home || '', path: '/index' },
        { label: bc.current || '', path: '' }
      ]
      const kw = this.$route?.query?.keyword
      if (kw) this.searchKeyword = String(kw)
      await this.loadDictData()
      this.loadList()
    },
    async loadDictData() {
      try {
        const response = await supplyDemandApi.getDictData(['yField', 'fjkjtarea'])
        const result = response.data || {}
        let dictData = []
        if (result.status === 0) {
          if (result.data && Array.isArray(result.data.rows)) dictData = result.data.rows
          else if (Array.isArray(result.data)) dictData = result.data
          else if (result.data && typeof result.data === 'object') dictData = Object.values(result.data).flat()
        }
        if (!Array.isArray(dictData)) dictData = []

        const allLabel = this.config.fieldFilter?.allLabel || CATEGORY_ALL_LABEL
        const fieldDict = dictData
          .filter(item => item && item.dictCode === 'yField')
          .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
        this.fieldTabs = [
          { label: allLabel, value: '' },
          ...fieldDict.map(item => ({
            label: item.itemValue || '',
            value: item.itemCode || ''
          })).filter(opt => opt.value)
        ]
        this.areaDict = dictData.filter(item => item && item.dictCode === 'fjkjtarea')
        this.activeField = ''
      } catch (e) {
        console.error('加载字典失败:', e)
        this.fieldTabs = [{ label: this.config.fieldFilter?.allLabel || '全部', value: '' }]
        this.areaDict = []
      }
    },
    resolveFieldLabel(codes) {
      if (!codes) return ''
      const list = String(codes).split(',').map(s => s.trim()).filter(Boolean)
      if (!list.length) return ''
      return list.map(code => {
        const hit = this.fieldTabs.find(opt => opt.value === code)
        return hit ? hit.label : code
      }).filter(Boolean).join('、')
    },
    resolveRegionLabel(item) {
      const provinceCode = item.mailingCommunications || item.province || ''
      const cityCode = item.mailingCity || item.city || ''
      const districtCode = item.countyOrganId || item.district || ''
      const findLabel = (code) => {
        if (!code) return ''
        const hit = this.areaDict.find(d => String(d.itemCode) === String(code))
        return hit ? (hit.itemValue || '') : ''
      }
      const parts = [findLabel(provinceCode), findLabel(cityCode), findLabel(districtCode)].filter(Boolean)
      if (parts.length) return parts.join('')
      return item.areaName || item.regionName || item.region || ''
    },
    pickViews(item) {
      const raw = item?.pageViews ?? item?.viewCount ?? item?.browseCount ?? item?.views
      if (raw === undefined || raw === null || raw === '') return null
      return raw
    },
    pickPublishCount(item) {
      const raw =
        item?.publishedAchievementCount ??
        item?.publishAchievementCnt ??
        item?.publishCnt ??
        item?.achievementCnt ??
        item?.tecCnt ??
        item?.publishedCount ??
        item?.publishCount
      if (raw === undefined || raw === null || raw === '') return null
      return raw
    },
    pickTransformCount(item) {
      const raw =
        item?.convertedAchievementCount ??
        item?.transformAchievementCnt ??
        item?.transformCnt ??
        item?.transformCount ??
        item?.convertedCount ??
        item?.convertCnt
      if (raw === undefined || raw === null || raw === '') return null
      return raw
    },
    mapTalentItem(item) {
      const field = this.resolveFieldLabel(item.industrialSystem || item.field || item.yField)
      const region = this.resolveRegionLabel(item)
      return {
        id: item.id || item.expertId || item.userId,
        name: item.name || item.expertName || '',
        avatar: item.photoPath || item.photo || item.avatar || item.headImg || '',
        field: field || '',
        region: region || '',
        views: this.pickViews(item),
        publishCount: this.pickPublishCount(item),
        transformCount: this.pickTransformCount(item)
      }
    },
    async loadList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          mailingCommunications: '',
          field: this.activeField || '',
          type: '',
          name: String(this.searchKeyword || '').trim()
        }
        const response = await techTalentApi.getExpertPageList(params)
        const result = response.data || {}
        if (result.status === 0 && result.data) {
          const rows = Array.isArray(result.data.list)
            ? result.data.list
            : (Array.isArray(result.data.rows) ? result.data.rows : [])
          this.list = rows.map(item => this.mapTalentItem(item))
          this.totalCount = result.data.total || result.data.totalCount || 0
        } else {
          this.list = []
          this.totalCount = 0
        }
      } catch (e) {
        console.error('加载科技人才列表失败:', e)
        this.list = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    handleFieldChange(value) {
      this.activeField = value
      this.currentPage = 1
      this.loadList()
    },
    handleSearch() {
      this.currentPage = 1
      this.loadList()
    },
    handleModuleClick(module) {
      if (module?.id === 'home') this.$router.push('/index')
    },
    goDetail(item) {
      if (!item?.id) return
      this.$router.push({
        path: '/tech-talent/detail',
        query: { id: item.id }
      })
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadList()
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadList()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: #fff;
}

.list-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

.content-wrap {
  width: 1700px;
  margin-left: 110px;
  margin-right: auto;
  box-sizing: border-box;
}

::v-deep .header-banner {
  margin-bottom: 0;
}

.filter-panel {
  margin-top: 0;
  margin-bottom: 18px;
}

.field-bar {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 24px;
  background: rgba(160, 144, 60, 0.1);
  box-sizing: border-box;
}

.field-ico {
  width: 16px;
  height: 18px;
  margin-top: 5px;
  flex-shrink: 0;
}

.field-label {
  flex-shrink: 0;
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
}

.field-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.field-chip {
  height: 28px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 2px;
  background: transparent;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  line-height: 26px;
  box-sizing: border-box;

  &.active {
    background: rgba(138, 122, 40, 0.15);
    border: 1px solid rgba(160, 144, 60, 0.55);
    color: #8a7a28;
    font-weight: 600;
  }

  &:hover:not(.active) {
    color: #8c7c26;
  }
}

.list-wrap {
  margin-bottom: 40px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  width: 100%;
}

.section-empty {
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  background: #fff;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 28px;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn,
.page-number {
  min-width: 36px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &.active,
  &:hover:not(:disabled) {
    border-color: #a0903c;
    color: #8a7a28;
  }

  &.active {
    background: rgba(160, 144, 60, 0.12);
    font-weight: 600;
  }
}


.locale-zh-CN {
  .filter-label,
  .field-label,
  .region-label {
    font-size: 16px;
  }

  .filter-chip,
  .field-chip,
  .region-chip {
    font-size: 18px;
    height: 36px;
    line-height: 34px;
    padding: 0 20px;
  }

  .filter-ico,
  .field-ico,
  .region-ico {
    width: 18px;
    height: 18px;
  }

  .pagination-info {
    font-size: 15px;
  }

  .page-btn,
  .page-number {
    min-width: 40px;
    height: 36px;
    font-size: 14px;
  }
}
</style>
